import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  BrandFooter,
  BrandHeader,
  Wordmark,
} from "@/components/runboom/marks";
import { RaceCard } from "@/components/runboom/race-card";
import { heroRunner, upcomingRaces, type RaceEvent } from "@/lib/mock";
import { formatRaceTime } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/races/")({
  component: RacesIndex,
});

const TERRAIN_FILTERS = ["all", "road", "trail", "track"] as const;
const DISTANCE_FILTERS = ["all", "5K", "10K", "Half", "Marathon"] as const;

type Terrain = (typeof TERRAIN_FILTERS)[number];
type Distance = (typeof DISTANCE_FILTERS)[number];

/** Higher = more likely to BOOM at this race. */
function boomScore(race: RaceEvent): number {
  const pb = heroRunner.pbs.find((p) => p.distance === race.distanceLabel);
  if (!pb) return 0;
  // Predicted faster than PB → big score. Predicted slower → tiny score.
  const gap = pb.timeSeconds - race.yourPredictedSeconds;
  return gap;
}

function RacesIndex() {
  const [terrain, setTerrain] = useState<Terrain>("all");
  const [distance, setDistance] = useState<Distance>("all");

  const filtered = upcomingRaces.filter((r) => {
    if (terrain !== "all" && r.terrain !== terrain) return false;
    if (distance !== "all" && r.distanceLabel !== distance) return false;
    return true;
  });

  const ranked = [...filtered].sort((a, b) => boomScore(b) - boomScore(a));
  const suggested = ranked.slice(0, Math.min(2, ranked.length));
  const suggestedSlugs = new Set(suggested.map((r) => r.slug));
  const rest = ranked.filter((r) => !suggestedSlugs.has(r.slug));

  return (
    <main className="mx-auto max-w-[1280px] px-5 py-6 sm:px-8 sm:py-10">
      <BrandHeader rightTab="Races" rightTabTone="orange" />

      <div className="mt-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <Wordmark size="sm" />
        </Link>
        <nav className="flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.1em] text-fg-muted sm:gap-5">
          <Link to="/oracle" className="hidden hover:text-fg sm:inline">Oracle</Link>
          <Link to="/wall" className="hidden hover:text-fg sm:inline">Wall</Link>
          <Link
            to="/leaderboards"
            search={{ distance: "10K", city: "Cape Town" }}
            className="hidden hover:text-fg sm:inline"
          >
            Leaderboards
          </Link>
          <Link
            to="/me"
            className="rounded-full px-3 py-1.5 text-night transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: "var(--color-acid-lime)" }}
          >
            Connect Strava
          </Link>
        </nav>
      </div>

      <header className="mt-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--color-blood-orange)" }}>
          Live calendar · {filtered.length} upcoming
        </div>
        <h1 className="mt-3 font-display-italic font-display text-[56px] leading-[48px] tracking-[-0.015em] text-fg sm:text-[88px] sm:leading-[76px] lg:text-[120px] lg:leading-[100px]">
          THE CALENDAR.
        </h1>
        <p className="mt-6 max-w-[60ch] font-mono text-[12px] uppercase tracking-[0.12em] text-fg-muted">
          Every road, trail, and track race within reach.
          <span className="ml-1" style={{ color: "var(--color-acid-lime)" }}>
            Ranked by where the Oracle says you'll BOOM.
          </span>
        </p>
      </header>

      <section className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 border-y border-border py-4">
        <FilterGroup label="Terrain">
          {TERRAIN_FILTERS.map((t) => (
            <Chip
              key={t}
              active={terrain === t}
              onClick={() => setTerrain(t)}
            >
              {t === "all" ? "All" : t[0]!.toUpperCase() + t.slice(1)}
            </Chip>
          ))}
        </FilterGroup>
        <FilterGroup label="Distance">
          {DISTANCE_FILTERS.map((d) => (
            <Chip
              key={d}
              active={distance === d}
              onClick={() => setDistance(d)}
            >
              {d === "all" ? "All" : d}
            </Chip>
          ))}
        </FilterGroup>
      </section>

      {suggested.length > 0 ? (
        <section className="mt-8 sm:mt-10">
          <div className="flex items-baseline justify-between border-b border-border pb-4">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--color-acid-lime)" }}>
                Suggested for you · top {suggested.length}
              </div>
              <h2 className="mt-1 font-display text-[28px] leading-[28px] tracking-[-0.015em] text-fg sm:text-[40px] sm:leading-[40px]">
                WHERE YOU'RE READY TO BOOM
              </h2>
            </div>
          </div>
          <ol className="mt-6 flex flex-col gap-3">
            {suggested.map((race, i) => (
              <SuggestedRaceCard
                key={race.slug}
                race={race}
                rank={i + 1}
              />
            ))}
          </ol>
        </section>
      ) : null}

      {rest.length > 0 ? (
        <section className="mt-10 sm:mt-12">
          <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-fg-subtle">
            Everything else upcoming
          </div>
          <div className="mt-4 flex flex-col gap-3">
            {rest.map((race) => (
              <Link
                key={race.slug}
                to="/races/$slug"
                params={{ slug: race.slug }}
                className="block transition-transform hover:-translate-y-0.5"
              >
                <RaceCard race={race} />
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {filtered.length === 0 ? (
        <div className="mt-8 rounded-[18px] border border-dashed border-border bg-bg-elevated px-6 py-12 text-center font-mono text-[12px] uppercase tracking-[0.1em] text-fg-muted">
          No races match those filters yet · check back as the calendar fills
        </div>
      ) : null}

      <BrandFooter />
    </main>
  );
}

function SuggestedRaceCard({ race, rank }: { race: RaceEvent; rank: number }) {
  const pb = heroRunner.pbs.find((p) => p.distance === race.distanceLabel);
  const gap = pb ? pb.timeSeconds - race.yourPredictedSeconds : 0;
  const willBoom = gap > 0;

  return (
    <li className="relative">
      <Link
        to="/races/$slug"
        params={{ slug: race.slug }}
        className="block transition-transform hover:-translate-y-0.5"
      >
        <RaceCard race={race} className="border-accent/30" />
      </Link>
      <div className="pointer-events-none absolute -top-2 left-4 flex items-center gap-2 sm:left-5">
        <span className="rounded-full bg-accent px-2 py-0.5 font-mono text-[11px] uppercase tracking-[0.08em] text-white">
          #{rank}
        </span>
        {willBoom ? (
          <span className="rounded-full border border-positive/40 bg-positive/10 px-2 py-0.5 font-mono text-[11px] text-positive">
            BOOM −{formatRaceTime(Math.abs(gap))}
          </span>
        ) : (
          <span className="rounded-full border border-fg-subtle/40 bg-bg-sunken px-2 py-0.5 font-mono text-[11px] text-fg-subtle">
            on PB
          </span>
        )}
      </div>
    </li>
  );
}

function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="font-sans text-[11px] uppercase tracking-[0.08em] text-fg-subtle">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 font-sans text-[13px] transition-colors",
        active
          ? "border-accent bg-accent-soft text-accent"
          : "border-border bg-bg-elevated text-fg-muted hover:border-border-strong hover:text-fg",
      )}
    >
      {children}
    </button>
  );
}
